import React, { useEffect, useState } from "react";
import {
  Paper,
  Container,
  Grid,
  Typography,
  TextField,
  Autocomplete,
  Badge,
  IconButton,
  Drawer,
  InputAdornment,
  Button,
  Box,
} from "@mui/material";
import { withStyles } from "@material-ui/core/styles";
import { FcSearch } from "react-icons/fc";
import Cookies from "js-cookie";
import TabStatus from "./GSTStatusTab";
import { dataReconciliation, headerConfigReconciliation } from "./Utils";
import { userService } from "../userService";
import {
  gstr1DataEndpoint,
  gstr1ReconiliationEndpoint,
  gstr2ReconiliationEndpoint,
} from "../config";
import moment from "moment";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Header from "../CommonComps/Header";
import GSTReportsIcon from "../images/GSTImage.png";
import { themecolor } from "../Navigation";
import { styles } from "../Theme/Theme";
import { AlertBar } from "../Alert/AlertBar";
import LoadingComponent from "../Loader";

const Reconciliation12 = ({ classes }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const reduxState = useSelector((val) => val?.rootReducer);
  const restoredSite = reduxState?.siteReducer?.site;
  const endPoints = reduxState.permissionReducer?.data;

  const [pageNo, setPageNo] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(15);
  const [totalPages, setTotalPages] = React.useState(1);

  let financialYearMonth = reduxState?.fymReducer?.data || [];

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const [reconciliationData, setReconciliationData] = useState(null);
  const [tabStatus, setTabStatus] = useState("TOTAL");
  const [tabStatusList, setTabStatusList] = useState([]);
  const [title, setTitle] = useState(null);

  const [searchField, setSearchField] = useState(null);

  // ===========Alerts===================//
  const [alerts, setAlerts] = useState({
    visible: false,
    alertMessage: "",
    alertType: "",
  });

  const [state, setState] = React.useState({
    top: false,
    left: false,
    bottom: false,
    right: false,
  });

  // Function to show success alert bar
  const successAlert = (message) => {
    setAlerts({
      visible: true,
      alertMessage: message || "Data fetched successfully.",
      alertType: "success",
    });
  };

  // Function to show error alert bar
  const errorAlert = (message) => {
    setAlerts({
      visible: true,
      alertMessage: message || "Something went wrong",
      alertType: "error",
    });
  };

  const toggleDrawer = (anchor, open) => (event) => {
    setState({ ...state, [anchor]: open });
  };

  // Function to close alert bar
  const closeAlertBar = () => setAlerts({ ...alerts, visible: false });

  const getReconciliationDataAPI = async ({
    currentTable,
    GSTReconciliation,
    fromDate,
    toDate,
  }) => {
    try {
      const formattedFromDate = moment(fromDate).format("DD/MM/YYYY");
      const formattedToDate = moment(toDate).format("DD/MM/YYYY");
      let response = null;
      let endpoint = "";

      if (currentTable === "GSTR1ReportTable") {
        endpoint = gstr1ReconiliationEndpoint;
      } else if (currentTable === "GSTR2ReportTable") {
        endpoint = gstr2ReconiliationEndpoint;
      }

      if (endpoint) {
        response = await userService.getData(
          `${endpoint}?from=${formattedFromDate}&to=${formattedToDate}&siteId=${localStorage.getItem(
            "siteId"
          )}&companyId=${localStorage.getItem("companyId")}`
        );
      }

      if (response && response?.data && response?.data?.status) {
        setReconciliationData(response?.data?.data);
      } else {
      }
    } catch (error) {
      console.error("Something went wrong!", error);
    }
  };

  useEffect(() => {
    if (!location?.state) return;

    const { currentTable, GSTReconciliation, fromDate, toDate } =
      location.state;

    // if (!currentTable) return;

    setTitle(currentTable);

    getReconciliationDataAPI({
      currentTable,
      GSTReconciliation,
      fromDate,
      toDate,
    });
  }, [location]);

  useEffect(() => {
    if (!reconciliationData) return;

    // Calculate the status counts correctly from the given data object
    const statusCounts = {
      TOTAL:
        (reconciliationData.matchedCount || 0) +
        (reconciliationData.missingInPurchaseCount || 0) +
        (reconciliationData.partiallyMatchedCount || 0) +
        (reconciliationData.missingInGSTR1Count || 0),
      PARTIALLY_MATCHED: reconciliationData.partiallyMatchedCount || 0,
      MATCHED: reconciliationData.matchedCount || 0,
      MISSING_IN_BOOKS: reconciliationData.missingInPurchaseCount || 0,
      MISSING_IN_GST_PORTAL: reconciliationData.missingInGSTR1Count || 0,
    };

    // Create an array of summary objects based on the counts
    const updatedData = [
      {
        name: "All Transactions",
        length: statusCounts.TOTAL,
        status: "TOTAL",
        backgroundColor: "#a6cef0",
        data: [
          ...(reconciliationData?.matchedList || []).map((item) => ({
            ...item,
            tabStatus: "MATCHED",
            tabMessage: "All Matched",
            backgroundColor: "#e2ffe2",
          })),
          ...(reconciliationData?.partiallyMatchedList || []).map((item) => ({
            ...item,
            tabStatus: "PARTIALLY_MATCHED",
            tabMessage: "Partially Matched",
            backgroundColor: "#f5dfc1",
          })),
          ...(reconciliationData?.missingInPurchaseList || []).map((item) => ({
            ...item,
            igst: item?.integratedTaxAmount,
            cgst: item?.centralTaxAmount,
            sgst: item?.stateTaxAmount,
            tabStatus: "MISSING_IN_BOOKS",
            tabMessage: "Missing in Account Books",
            backgroundColor: "#ffffd7",
          })),
          ...(reconciliationData?.missingInGSTR1List || []).map((item) => ({
            ...item,
            tabStatus: "MISSING_IN_GST_PORTAL",
            tabMessage: "Missing in GST Portal",
            backgroundColor: "#ffc8bf",
          })),
        ],
      },
      {
        name: "Partial Matched",
        length: statusCounts.PARTIALLY_MATCHED,
        status: "PARTIALLY_MATCHED",
        backgroundColor: "#f5dfc1",
        data: (reconciliationData?.partiallyMatchedList || []).map((item) => ({
          ...item,
          tabStatus: "PARTIALLY_MATCHED",
          tabMessage: "Partially Matched",
          backgroundColor: "#f5dfc1",
        })),
      },
      {
        name: "Matched",
        length: statusCounts.MATCHED,
        status: "MATCHED",
        backgroundColor: "#e2ffe2",
        data: (reconciliationData?.matchedList || []).map((item) => ({
          ...item,
          tabStatus: "MATCHED",
          tabMessage: "All Matched",
          backgroundColor: "#e2ffe2",
        })),
      },
      {
        name: "Missing in Books",
        length: statusCounts.MISSING_IN_BOOKS,
        status: "MISSING_IN_BOOKS",
        backgroundColor: "#ffffd7",
        data: (reconciliationData?.missingInPurchaseList || []).map((item) => ({
          ...item,
          igst: item?.integratedTaxAmount,
          cgst: item?.centralTaxAmount,
          sgst: item?.stateTaxAmount,
          tabStatus: "MISSING_IN_BOOKS",
          tabMessage: "Missing in Account Books",
          backgroundColor: "#ffffd7",
        })),
      },
      {
        name: "Missing in GST Portal",
        length: statusCounts.MISSING_IN_GST_PORTAL,
        status: "MISSING_IN_GST_PORTAL",
        backgroundColor: "#ffc8bf",
        data: (reconciliationData?.missingInGSTR1List || []).map((item) => ({
          ...item,
          tabStatus: "MISSING_IN_GST_PORTAL",
          tabMessage: "Missing in GST Portal",
          backgroundColor: "#ffc8bf",
        })),
      },
    ];

    setTabStatusList(updatedData);
  }, [reconciliationData]);

  const tabStatusHandler = (selectedTab) => {
    setTabStatus(selectedTab.status);
  };

  const localSearchHandle = () => {
    const sessionData = Cookies.get("gstSession");

    let parsedSessionData;
    try {
      parsedSessionData = sessionData ? JSON.parse(sessionData) : null;
    } catch (error) {
      console.error("Error parsing session data:", error);
      parsedSessionData = null;
    }

    if (parsedSessionData?.gstLogin) {
      getReconciliationDataAPI({
        currentTable: title,
        GSTReconciliation: parsedSessionData?.gstLogin,
        fromDate: moment().startOf("month").format("YYYY-MM-DD"),
        toDate: moment().endOf("month").format("YYYY-MM-DD"),
      });
    }
  };

  const handleKeyPress = (e) => {
    const sessionData = Cookies.get("gstSession");
    let parsedSessionData;
    try {
      parsedSessionData = sessionData ? JSON.parse(sessionData) : null;
    } catch (error) {
      console.error("Error parsing session data:", error);
      parsedSessionData = null;
    }

    if (e.key === "Enter" && parsedSessionData?.gstLogin) {
      if (pageNo === 1) {
        getReconciliationDataAPI({
          currentTable: title,
          GSTReconciliation: parsedSessionData?.gstLogin,
          fromDate: moment().startOf("month").format("YYYY-MM-DD"),
          toDate: moment().endOf("month").format("YYYY-MM-DD"),
        });
      } else {
        setPageNo(1);
      }
    }
  };

  return (
    <>
      <Paper
        className={classes.paper}
        elevation={0}
        style={{ width: "100%", overflow: "auto" }}
      >
        <Container style={{ maxWidth: "100%" }}>
          <Grid container style={{ margin: "0", alignItems: "center" }}>
            <Header
              gridSize="3"
              source={GSTReportsIcon}
              headerText={
                title === "GSTR1ReportTable"
                  ? "GSTR-1 Reconciliation"
                  : "GSTR-2 Reconciliation"
              }
            />
            <Grid
              item
              xs={9}
              container
              alignItems="center"
              justifyContent="flex-end"
              spacing={1}
            >
              {/* {GSTReconciliation && ( */}
              <Grid item>
                <Button
                  variant="contained"
                  style={{
                    background: themecolor,
                    color: "#fff",
                    marginRight: "10px",
                  }}
                  onClick={() => navigate("/gst/gst-details-portal")}
                >
                  GST Menu
                </Button>
              </Grid>
              {/* <Grid item>
                <TextField
                  value={searchField}
                  onChange={(e) => setSearchField(e.target.value)}
                  margin="dense"
                  size="small"
                  style={{
                    maxWidth: 250,
                  }}
                  label="Search"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={localSearchHandle}
                        >
                          <FcSearch />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  onKeyPress={(e) => handleKeyPress(e)}
                />
              </Grid> */}
            </Grid>

            <Grid item xs={12} sx={{ marginTop: "10px", padding: "20px 10px" }}>
              <TabStatus
                title={title}
                tabStatus={tabStatus}
                setTabStatus={setTabStatus}
                tabStatusHandler={tabStatusHandler}
                header={headerConfigReconciliation}
                data={tabStatusList}
                pageNo={pageNo}
                setPageNo={setPageNo}
                totalPages={totalPages}
                setTotalPages={setTotalPages}
                pageSize={pageSize}
                setPageSize={setPageSize}
                navigate={navigate}
              />
            </Grid>
          </Grid>
        </Container>
      </Paper>
      {/* ==============Alerts=============== */}
      <AlertBar
        visible={alerts?.visible}
        closeAlertBar={closeAlertBar}
        alertMessage={alerts?.alertMessage}
        alertType={alerts?.alertType}
      />
      {/* =============Loader=============== */}
      {loading && <LoadingComponent />}
    </>
  );
};

export default withStyles(styles)(Reconciliation12);
